import { Box, Grid2, Typography } from '@mui/material';
import AutocompleteField from 'components/AutocompleteField';
import { AlertDialog, CheckboxComponent, CheckboxTerms, PhoneNumber } from '@utriper/core-ui';
import TermsAndConditions from 'components/TermsAndConditions';
import { useCheckout } from 'core/contexts/checkout';
import { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from '../../../../core/contexts/localization';
import { nationalityList } from 'core/constants';
import { useStripe } from 'core/contexts/stripe';
import TextField from 'components/TextField';
import StripeForm from '../StripeForm';
import OrderSummary from '../OrderSummary';
import { useUser } from 'core/contexts/user';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles({
    textField: {
        marginBottom: '1rem',
    },
    phoneField: {
        width: '100%',
    },
});

const CheckoutForm = () => {
    const { t: translate } = useTranslation();
    const { bookingItems, paymentId, onDismissConfirmPayment, termsToAccept, getExperienceTermsId } = useCheckout();
    const { userData, setUserData, submitted, formErrors, setFormErrors, agreeWithTermsAndConditions, setAgreeWithTermsAndConditions, experiencesAgreeWithTermsAndConditions, setExperiencesAgreeWithTermsAndConditions } = useUser();
    const { stripeOpen, setStripeOpen, stripePromise, stripeClientSecret } = useStripe();
    const classes = useStyles();

    const [openModal, setOpenModal] = useState(false);
    const [onTermsOpen, setOnTermsOpen] = useState(false);

    useEffect(() => {
        if (bookingItems.length > 0) {
            const { firstName, lastName, email, mobilePhone, mobilePhoneCountryCode } = bookingItems[0];

            setUserData((prev) => ({
                ...prev,
                firstName: firstName || '',
                lastName: lastName || '',
                email: email || '',
                phoneNumber: mobilePhone || '',
                phoneNumberCountryCode: mobilePhoneCountryCode || '',
            }));

            setFormErrors((prev) => ({
                ...prev,
                firstName: firstName ? false : true,
                lastName: lastName ? false : true,
                email: email ? false : true,
                phoneNumber: mobilePhone ? false : true,
                phoneNumberCountryCode: mobilePhoneCountryCode ? false : true,
            }));
        }
    }, [bookingItems]);

    const validate = (value) => {
        return value;
    };

    const validateEmail = (email) => {
        if (!email) return false;
        return String(email)
            .toLowerCase()
            .match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
    };

    const renderTermsAndConditions = () => {
        return (
            <Grid2 container flexWrap="wrap" justifyContent="flex-start" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: '1rem' }}>{translate('termsAndConditions.accept')}</Typography>
                <Typography
                    sx={{ fontSize: '1rem', fontWeight: 'bold' }}
                    onClick={(e) => {
                        setOpenModal(true);
                        e.preventDefault();
                    }}>
                    {`${translate('termsAndConditions.link')}`}
                </Typography>
            </Grid2>
        );
    };

    const handleChange = (field, value) => {
        const auxUserData = {
            ...userData,
            [field]: value,
        };

        setUserData(auxUserData);
    };

    return (
        <Box sx={{ display: 'flex', width: { xs: '90vw', md: '100vw' }, height: { xs: '120vh', md: '95vh' }, justifyContent: 'center', alignItems: 'flex-start', marginTop: { xs: '4em', md: '3.5em' }, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ width: { xs: '90%', md: '40vw' }, marginRight: '1.5em', marginLeft: '1.5em' }}>
                <Box style={{ fontSize: '2rem', marginBottom: '2rem' }}>
                    <strong>{bookingItems[0]?.priceTotal === 0 ? translate('checkout.order_details') : translate('checkout.billing_details')}</strong>
                </Box>
                <Grid2>
                    <Form
                        onSubmit={() => {}}
                        initialValues={userData}
                        render={({ handleSubmit, form }) => (
                            <form onSubmit={handleSubmit}>
                                <Grid2 container>
                                    <TextField
                                        name="firstName"
                                        required
                                        label={translate('checkout.first_name')}
                                        validate={validate}
                                        className={classes.textField}
                                        onTextFieldChange={(event) => {
                                            handleChange('firstName', event.target.value);
                                        }}
                                        errorMessage={(value) => translate('form.required')}
                                        setFormError={(value) =>
                                            setFormErrors({
                                                ...formErrors,
                                                firstName: value,
                                            })
                                        }
                                        initialError={formErrors.firstName && submitted}
                                    />
                                    <TextField
                                        name="lastName"
                                        required
                                        label={translate('checkout.last_name')}
                                        validate={validate}
                                        className={classes.textField}
                                        onTextFieldChange={(event) => {
                                            handleChange('lastName', event.target.value);
                                        }}
                                        errorMessage={(value) => translate('form.required')}
                                        setFormError={(value) =>
                                            setFormErrors({
                                                ...formErrors,
                                                lastName: value,
                                            })
                                        }
                                        initialError={formErrors.lastName && submitted}
                                    />
                                    <TextField
                                        name="email"
                                        required
                                        label="Email"
                                        validate={validateEmail}
                                        className={classes.textField}
                                        onTextFieldChange={(event) => {
                                            handleChange('email', event.target.value);
                                        }}
                                        errorMessage={(value) => {
                                            if (!value) return translate('form.required');
                                            if (value.length > 0) return translate('form.invalid.email');
                                        }}
                                        setFormError={(value) =>
                                            setFormErrors({
                                                ...formErrors,
                                                email: value,
                                            })
                                        }
                                        initialError={formErrors.email && submitted}
                                    />
                                    <Field
                                        id="phone"
                                        onFieldChange={(value, country) => {
                                            const auxUserData = {
                                                ...userData,
                                                phoneNumber: value,
                                                phoneNumberCountryCode: country,
                                            };
                                            setUserData(auxUserData);
                                        }}
                                        className={`${classes.textField} ${classes.phoneField}`}
                                        name="phoneNumber"
                                        component={PhoneNumber}
                                        label={translate('checkout.phone_number') + ' *'}
                                        initialCountry={'us'}
                                        validateField={(value, countryCode) => value.length > 0 && value.startsWith(countryCode) && value.length > countryCode.length}
                                        setFormError={(value) =>
                                            setFormErrors({
                                                ...formErrors,
                                                phoneNumber: value,
                                                phoneNumberCountryCode: value,
                                            })
                                        }
                                        errorMessage={translate('form.required')}
                                        initialError={formErrors.phoneNumber && formErrors.phoneNumberCountryCode && submitted}
                                    />
                                    <AutocompleteField
                                        className={{ width: '100%', color: 'red' }}
                                        name="nationality"
                                        label={translate('checkout.country') + ' *'}
                                        options={nationalityList}
                                        getOptionLabels={(option) => option.en_short_name}
                                        required
                                        validate={validate}
                                        errorMessage={translate('form.required')}
                                        onFieldChange={(event) => {
                                            const auxUserData = {
                                                ...userData,
                                                country: event.target.textContent,
                                            };
                                            setUserData(auxUserData);
                                        }}
                                        setFormError={(value) =>
                                            setFormErrors({
                                                ...formErrors,
                                                country: value,
                                            })
                                        }
                                        initialError={formErrors.country && submitted}
                                    />
                                    <Grid2 container flexDirection="column" spacing={2} pt={'2em'}>
                                        <Field
                                            hasError={submitted && !agreeWithTermsAndConditions}
                                            errorMessage={translate('form.required')}
                                            checked={agreeWithTermsAndConditions}
                                            onChange={(value) => {
                                                setAgreeWithTermsAndConditions(value);
                                            }}
                                            name="termsAndConditions"
                                            component={CheckboxComponent}
                                            labelComponent={renderTermsAndConditions()}
                                        />

                                        {termsToAccept?.length > 0 && (
                                            <Field
                                                checked={experiencesAgreeWithTermsAndConditions}
                                                onChange={(value) => {
                                                    setExperiencesAgreeWithTermsAndConditions(value);
                                                }}
                                                name="experiencesTermsAndConditions"
                                                component={CheckboxTerms}
                                                termsToAccept={termsToAccept}
                                                onTermsClick={getExperienceTermsId}
                                                fileUrl={process.env.REACT_APP_CDN_URL}
                                            />
                                        )}
                                    </Grid2>
                                </Grid2>
                            </form>
                        )}
                    />
                </Grid2>
            </Box>
            {bookingItems.length ? <OrderSummary /> : null}
            <TermsAndConditions
                open={openModal}
                onClose={() => setOpenModal(false)}
                onClick={() => {
                    setAgreeWithTermsAndConditions(!agreeWithTermsAndConditions);
                    setOpenModal(false);
                }}
            />
            <AlertDialog
                open={stripeOpen}
                dialogTitle={'Booking Confirmation'}
                hideTitle={true}
                dialogContent={<StripeForm open={stripeOpen} stripePromise={stripePromise} clientSecret={stripeClientSecret} />}
                handleClose={() => {
                    onDismissConfirmPayment(paymentId);
                    setStripeOpen(false);
                }}
                handleSubmit={() => {}}
                labelBack="Cancel"
                hideBack={false}
                labelNext="Ok"
                hideSubmit={true}
                maxWidth={'sm'}
                fullWidth
            />
        </Box>
    );
};

export default CheckoutForm;
